import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";

const NotFound = () => {
  const navigate = useNavigate();

  return (
    <>
      <div className="bg-[#0B1C2D] text-white antialiased overflow-x-hidden min-h-screen flex flex-col font-sans relative">
        {/* BACKGROUND EFFECTS */}
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="absolute inset-0 bg-grid-pattern bg-[size:40px_40px] opacity-[0.07]"></div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[800px] bg-[radial-gradient(circle_at_center,rgba(230,57,70,0.2)_0%,rgba(11,28,45,0.4)_50%,rgba(11,28,45,0)_70%)] opacity-60"></div>
          <div className="absolute -top-[200px] -right-[200px] w-[600px] h-[600px] bg-[#E63946]/10 rounded-full blur-[120px] animate-pulse-glow"></div>
          <div
            className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#1E3A8A]/20 rounded-full blur-[100px] animate-pulse-glow"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>
        {/* MAIN CONTENT */}
        <main className="flex-grow flex items-center justify-center relative z-10 px-4 sm:px-6 lg:px-8 py-20 sm:py-12 lg:py-20">
          <div className="w-[95%] md:w-[90%] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* TEXT COLUMN */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-6 flex flex-col items-center lg:items-start text-center lg:text-left"
            >
              <Link
                to="/"
                className="flex items-center gap-3 group cursor-pointer mb-6"
              >
                <div className="flex items-center justify-center size-10 rounded-lg bg-gradient-to-br from-[#E63946] to-[#D62839] text-white transition-transform duration-300 group-hover:scale-110 shadow-lg shadow-[#E63946]/20 relative overflow-hidden">
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                  <span className="material-symbols-outlined relative z-10">
                    hub
                  </span>
                </div>
                <span className="text-xl font-bold tracking-tight text-white group-hover:text-white/90 transition-colors">
                  ContactHQ
                </span>
              </Link>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-[#E63946] mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E63946] animate-pulse"></span>
                Error 404
              </div>

              <h1 className="text-4xl sm:text-6xl lg:text-8xl font-bold tracking-tight text-white mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-gray-400">
                  Lost in
                </span>
                <br className="hidden lg:block" />
                <span className="relative inline-block text-[#E63946] ml-2 lg:ml-0">
                  Space?
                  <svg
                    className="absolute w-full h-3 -bottom-1 left-0 text-[#E63946] opacity-60"
                    preserveAspectRatio="none"
                    viewBox="0 0 100 10"
                  >
                    <path
                      d="M0 5 Q 50 10 100 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    ></path>
                  </svg>
                </span>
              </h1>

              <p className="text-lg text-gray-400 max-w-lg mt-6 mb-10 leading-relaxed">
                It looks like the page you're looking for has drifted away or
                never existed. Don't worry, our automated systems are already
                recalibrating.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                <button
                  onClick={() => navigate(-1)}
                  className="w-full sm:w-auto group relative flex items-center justify-center gap-2 rounded-lg bg-[#E63946] px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-[#E63946]/20 hover:bg-[#D62839] hover:-translate-y-1 hover:shadow-xl hover:shadow-[#E63946]/30 transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer"></div>
                  <span className="material-symbols-outlined text-[20px] transition-transform group-hover:-translate-x-1">
                    arrow_back
                  </span>
                  Go Back
                </button>

                <Link
                  to="/"
                  className="w-full sm:w-auto group flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 px-8 py-3.5 text-base font-semibold text-white hover:bg-white/10 hover:border-white/20 hover:-translate-y-1 transition-all duration-300 backdrop-blur-sm"
                >
                  <span className="material-symbols-outlined text-gray-400 group-hover:text-[#E63946] transition-colors text-[20px]">
                    home
                  </span>
                  Return Home
                </Link>
              </div>

              <div className="mt-12 pt-8 border-t border-white/5 w-full">
                <p className="text-sm text-gray-500 mb-4 font-medium uppercase tracking-wider">
                  Helpful Links
                </p>
                <div className="flex flex-wrap justify-center lg:justify-start gap-x-8 gap-y-3 text-sm font-medium text-gray-400">
                  {["Status Page", "Contact Support", "API Documentation"].map(
                    (link) => (
                      <a
                        key={link}
                        href="#"
                        className="hover:text-[#E63946] transition-colors flex items-center gap-1.5 group"
                      >
                        {link}
                        <span className="material-symbols-outlined text-[16px] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#E63946]">
                          arrow_forward
                        </span>
                      </a>
                    )
                  )}
                </div>
              </div>
            </motion.div>

            {/* ILLUSTRATION COLUMN */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="lg:col-span-6 relative h-[400px] lg:h-[600px] flex items-center justify-center"
            >
              <div className="relative w-full max-w-md aspect-square">
                {/* Main 404 Text */}
                <div className="absolute inset-0 flex items-center justify-center z-10 animate-float">
                  <div className="relative">
                    <h2 className="text-[180px] lg:text-[240px] font-bold text-transparent bg-clip-text bg-gradient-to-b from-white/10 to-white/0 select-none leading-none tracking-tighter">
                      404
                    </h2>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full text-center">
                      <span className="material-symbols-outlined text-8xl lg:text-9xl text-[#E63946] drop-shadow-[0_0_30px_rgba(230,57,70,0.5)]">
                        satellite_alt
                      </span>
                    </div>
                  </div>
                </div>

                {/* Floating Card 1 (Warning) */}
                <div
                  className="absolute top-[15%] right-[5%] z-20 animate-float-slow"
                  style={{ animationDelay: "300ms" }}
                >
                  <div className="glass-panel p-4 rounded-xl shadow-2xl shadow-black/30 border-l-4 border-l-[#E63946] max-w-[200px] transform rotate-3 hover:rotate-0 transition-transform duration-500">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center text-[#E63946]">
                        <span className="material-symbols-outlined text-sm">
                          warning
                        </span>
                      </div>
                      <div className="h-2 w-20 bg-white/10 rounded-full"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-1.5 w-full bg-white/5 rounded-full"></div>
                      <div className="h-1.5 w-2/3 bg-white/5 rounded-full"></div>
                    </div>
                  </div>
                </div>

                {/* Floating Card 2 (System Status) */}
                <div
                  className="absolute bottom-[20%] left-[0%] z-20 animate-float-slow"
                  style={{ animationDelay: "700ms" }}
                >
                  <div className="glass-panel p-4 rounded-xl shadow-2xl shadow-black/30 flex items-center gap-4 max-w-[220px] transform -rotate-2 hover:rotate-0 transition-transform duration-500">
                    <div className="bg-green-500/20 p-2 rounded-lg text-green-400">
                      <span className="material-symbols-outlined">dns</span>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-medium mb-1">
                        System Status
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                        <span className="text-sm font-bold text-white">
                          Operational
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Icon 3 */}
                <div
                  className="absolute top-[30%] left-[10%] z-10 animate-float"
                  style={{ animationDelay: "100ms" }}
                >
                  <div className="glass-panel w-12 h-12 rounded-lg flex items-center justify-center rotate-12">
                    <span className="material-symbols-outlined text-gray-400 opacity-50">
                      search_off
                    </span>
                  </div>
                </div>

                {/* Rotating Rings */}
                <div className="absolute top-1/2 left-1/2 w-[120%] h-[120%] border border-white/5 rounded-full animate-spin-slow"></div>
                <div className="absolute top-1/2 left-1/2 w-[90%] h-[90%] border border-dashed border-white/10 rounded-full animate-spin-slow-reverse"></div>
              </div>
            </motion.div>
          </div>
        </main>
      </div>
    </>
  );
};

export default NotFound;
